// PanelHost — renders the left panel's active view (US-874 shell · corrected).
//
// The rail tabs (UIContext.activeView) switch which view shows here. Map Config
// is the live US-875 layer panel; Analysis is the US-879 country panel;
// Definitions / About are scaffolds their feature US fill. Replaces the removed
// right panel — all panel content lives in this single tabbed column per the
// mockup.
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useUI from "../../hooks/useUI";
import LayerToggle from "../layerToggle/LayerToggle";
import GeographicFocus from "./GeographicFocus";
import AnalysisPanel from "../analysis/AnalysisPanel";

// Section header matching the mockup's panel eyebrow + title. Exported for the
// views that own their header per selection state (AnalysisPanel, US-879).
export const ViewHeader = ({ eyebrow, title, subtitle }) => (
  <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
    <Typography
      component="p"
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {eyebrow}
    </Typography>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, fontSize: "1.375rem", lineHeight: 1.2, mt: 0.25 }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mt: 0.75, lineHeight: 1.45 }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Placeholder for views whose content is owned by a not-yet-built feature US.
const Placeholder = ({ owner }) => (
  <Box sx={{ px: 2, pb: 2 }}>
    <Typography variant="body2" sx={{ color: "text.disabled" }}>
      Coming in {owner}.
    </Typography>
  </Box>
);

const MapConfigView = () => (
  <>
    <ViewHeader
      eyebrow="Map Configuration"
      title="Layers & Levels"
      subtitle="Set the geographic focus, then toggle data layers. Changes apply immediately."
    />
    <GeographicFocus />
    <LayerToggle />
  </>
);

const AnalysisView = () => <AnalysisPanel />;

const DefinitionsView = () => (
  <>
    <ViewHeader eyebrow="Reference" title="Definitions" />
    <Placeholder owner="US-875/876 (layer definitions)" />
  </>
);

const AboutView = () => (
  <>
    <ViewHeader eyebrow="About" title="Connectivity Mapping Tool" />
    <Placeholder owner="a later US" />
  </>
);

const VIEWS = {
  "map-config": MapConfigView,
  analysis: AnalysisView,
  definitions: DefinitionsView,
  about: AboutView,
};

const PanelHost = () => {
  const { activeView } = useUI();
  const View = VIEWS[activeView] || MapConfigView;
  return <View />;
};

export default PanelHost;
